import React, { useEffect, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import DatePicker from '@cloudscape-design/components/date-picker';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import { getAnalyticsService, getAsnMetricsService, getUploadService } from '../services';
import type { AsnFailure, AsnMetrics, IntegrationCounts } from '../services/types';

// Supplier Gateway · ASN (Advance Shipping Notice). Summary/worklist render
// empty/zero states until the backend is wired; the upload uses the real
// upload service.

type Preset = '24h' | '7d' | '30d';
type Integration = 'API' | 'File Drop' | 'Presigned URL';

// Selected date range: one of the presets, or a custom From/To window.
type ActiveRange =
    | { kind: 'preset'; preset: Preset }
    | { kind: 'custom'; from: string; to: string; days: number };

const INTEGRATIONS: Integration[] = ['API', 'File Drop', 'Presigned URL'];
const PRESETS: Preset[] = ['24h', '7d', '30d'];

const TAG_BY_INTEGRATION: Record<Integration, string> = {
    API: 'REST',
    'File Drop': 'CSV or Excel',
    'Presigned URL': 'S3',
};

// UI label -> backend integration/source key returned by the metrics API.
const SOURCE_BY_INTEGRATION: Record<Integration, string> = {
    API: 'API',
    'File Drop': 'FILE_DROP',
    'Presigned URL': 'PRESIGNED_URL',
};

const EMPTY_COUNTS: IntegrationCounts = { total: 0, dataErrors: 0, businessErrors: 0 };

// Resolve the active range to an ISO [from, to] window the metrics API expects.
function rangeToWindow(r: ActiveRange): { from: string; to: string } {
    const now = new Date();
    const to = now.toISOString();
    if (r.kind === 'custom') {
        // Inclusive day window: start of `from` to end of `to`.
        return {
            from: new Date(`${r.from}T00:00:00Z`).toISOString(),
            to: new Date(`${r.to}T23:59:59Z`).toISOString(),
        };
    }
    const daysByPreset: Record<Preset, number> = { '24h': 1, '7d': 7, '30d': 30 };
    const from = new Date(now.getTime() - daysByPreset[r.preset] * 86400000).toISOString();
    return { from, to };
}

const rangeLabel = (r: ActiveRange): string =>
    r.kind === 'preset' ? r.preset : `${r.from} → ${r.to}`;

// Inclusive day count between two yyyy-mm-dd dates; 0 if invalid/reversed.
function daysBetween(from: string, to: string): number {
    if (!from || !to) return 0;
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
}

// "Coming up" horizon options for the Pending ASNs worklist.
const PENDING_HORIZONS = [7, 14, 30, 'all'] as const;
type Horizon = (typeof PENDING_HORIZONS)[number];
const horizonLabel = (h: Horizon) => (h === 'all' ? '30+' : `${h}d`);

// CSV export columns for the pending worklist.
const PENDING_EXPORT_HEADERS = [
    'Line #',
    'PO',
    'Item',
    'Description',
    'Ordered',
    'Received',
    'Outstanding',
    'Need by date',
    'Bucket',
];

function downloadCsv(filename: string, content: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/** Human-readable file size (e.g. 1536 -> "1.5 KB"). */
function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
    }
    return `${value.toFixed(1)} ${units[unit]}`;
}

type Bucket = 'data' | 'business';

// ── Per-integration summary card ──────────────────────────────────────────────
function FailCount({
    label,
    count,
    color,
    onClick,
}: {
    label: string;
    count: number;
    color: 'warning' | 'error';
    onClick: () => void;
}) {
    if (count === 0) {
        return (
            <SpaceBetween size="xxxs">
                <Box fontSize="heading-l" color="text-status-inactive">
                    0
                </Box>
                <Box color="text-body-secondary" fontSize="body-s">
                    {label}
                </Box>
            </SpaceBetween>
        );
    }
    return (
        <SpaceBetween size="xxxs">
            <Button variant="inline-link" onClick={onClick}>
                <Box
                    variant="span"
                    fontSize="heading-l"
                    fontWeight="bold"
                    color={color === 'error' ? 'text-status-error' : 'text-status-warning'}
                >
                    {String(count)}
                </Box>
            </Button>
            <Box color="text-body-secondary" fontSize="body-s">
                {label}
            </Box>
        </SpaceBetween>
    );
}

function IntegrationCard({
    intg,
    counts,
    onViewFailures,
}: {
    intg: Integration;
    counts: IntegrationCounts;
    onViewFailures: (bucket: Bucket) => void;
}) {
    const { total, dataErrors, businessErrors } = counts;
    return (
        <Container
            header={
                <Header variant="h3" info={<Badge color="blue">{TAG_BY_INTEGRATION[intg]}</Badge>}>
                    {intg}
                </Header>
            }
        >
            <SpaceBetween size="m">
                <SpaceBetween size="xxxs">
                    <Box color="text-body-secondary" fontSize="body-s">
                        Total ASNs
                    </Box>
                    <Box fontSize="display-l" fontWeight="bold">
                        {String(total)}
                    </Box>
                </SpaceBetween>
                <ColumnLayout columns={2}>
                    <FailCount
                        label="Data errors"
                        count={dataErrors}
                        color="warning"
                        onClick={() => onViewFailures('data')}
                    />
                    <FailCount
                        label="Business errors"
                        count={businessErrors}
                        color="error"
                        onClick={() => onViewFailures('business')}
                    />
                </ColumnLayout>
            </SpaceBetween>
        </Container>
    );
}

// ── Pending ASNs (empty until backend is wired) ───────────────────────────────
function PendingSection({
    title,
    tone,
    note,
    emptyText,
}: {
    title: string;
    tone: 'overdue' | 'coming';
    note: string;
    emptyText: string;
}) {
    return (
        <Container
            header={
                <Header variant="h3" description={note}>
                    {title}
                </Header>
            }
        >
            <Box textAlign="center" color="text-body-secondary" padding="l">
                <SpaceBetween size="xs">
                    <StatusIndicator type={tone === 'overdue' ? 'success' : 'info'}>
                        {tone === 'overdue' ? 'All caught up' : 'Nothing scheduled'}
                    </StatusIndicator>
                    <Box color="text-body-secondary">{emptyText}</Box>
                </SpaceBetween>
            </Box>
        </Container>
    );
}

function PendingAsns() {
    const [horizon, setHorizon] = useState<Horizon>(14);

    // No backend yet, so both sections are empty. Export still produces a valid
    // (header-only) CSV so the flow works end-to-end.
    const exportCsv = () =>
        downloadCsv('pending-asns.csv', `${PENDING_EXPORT_HEADERS.join(',')}\n`);

    return (
        <Container
            header={
                <Header
                    variant="h2"
                    description="What still needs an ASN: everything overdue, plus what's coming up."
                    actions={
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                            <Box color="text-body-secondary">Coming up:</Box>
                            <SegmentedControl
                                selectedId={String(horizon)}
                                onChange={({ detail }) =>
                                    setHorizon(
                                        detail.selectedId === 'all'
                                            ? 'all'
                                            : (Number(detail.selectedId) as Horizon),
                                    )
                                }
                                options={PENDING_HORIZONS.map((h) => ({
                                    id: String(h),
                                    text: horizonLabel(h),
                                }))}
                            />
                            <Button iconName="download" onClick={exportCsv}>
                                Export
                            </Button>
                        </SpaceBetween>
                    }
                >
                    Pending ASNs
                </Header>
            }
        >
            <ColumnLayout columns={2}>
                <PendingSection
                    title={horizon === 'all' ? 'Overdue 30+ days' : 'Overdue'}
                    tone="overdue"
                    note={
                        horizon === 'all'
                            ? 'overdue by more than 30 days — escalate'
                            : 'past need-by — ship and notify now'
                    }
                    emptyText={
                        horizon === 'all'
                            ? 'Nothing overdue by more than 30 days.'
                            : 'Nothing overdue.'
                    }
                />
                <PendingSection
                    title={horizon === 'all' ? 'Due beyond 30 days' : 'Coming up'}
                    tone="coming"
                    note={
                        horizon === 'all'
                            ? 'need-by more than 30 days out'
                            : `due within the next ${horizon} days`
                    }
                    emptyText={
                        horizon === 'all'
                            ? 'Nothing due beyond 30 days.'
                            : `Nothing due in the next ${horizon} days.`
                    }
                />
            </ColumnLayout>
        </Container>
    );
}

// Max upload size for the browser file-drop channel. Keep this in lockstep with
// the "up to 25 MB" hint shown in the drop zone.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

// Downloadable CSV template for the File Drop channel. Header + two example
// rows, matching the ASN CSV spec (SupplierConnectService/ASN_CSV_SPEC.md) and
// the sample at SupplierConnectService/sample_asn_filedrop.csv — keep these in
// lockstep with that contract (the column names are what the normalizer reads).
// Generated client-side so there is no static asset to ship or keep in sync;
// Excel opens the .csv directly, and a supplier who wants to upload .xlsx can
// "Save As" Excel from it.
// fileId / fileCreationDate are NOT columns: the server generates the file
// envelope (file id = submissionId, creation date = receipt time), so suppliers
// only provide shipment data. Starts at asnNumber.
const ASN_TEMPLATE_HEADERS = [
    'asnNumber',
    'shipFromPartyId',
    'shipFromName',
    'shipToLine1',
    'shipToCity',
    'shipToState',
    'shipToPostalCode',
    'shipToCountry',
    'palletId',
    'cartonId',
    'poNumber',
    'lineNumber',
    'partno',
    'itemDescription',
    'shippedQuantity',
    'unitPrice',
    'countryOfOrigin',
    'serialNumbers',
];
const ASN_TEMPLATE_ROWS = [
    [
        'ASN-2026-000999001',
        'VENDOR-1001',
        'Acme Manufacturing Co.',
        '20529 62nd Ave S',
        'Kent',
        'WA',
        '98032',
        'US',
        'SSCC-001',
        'SSCC-002',
        'PO-4501234567',
        '10',
        'PART-ABC-10',
        'USB-C to USB-A Adapter 3ft',
        '3',
        '10',
        'CN',
        'SN-0001|SN-0002|SN-0003',
    ],
    [
        'ASN-2026-000999001',
        'VENDOR-1001',
        'Acme Manufacturing Co.',
        '20529 62nd Ave S',
        'Kent',
        'WA',
        '98032',
        'US',
        'SSCC-001',
        'SSCC-002',
        'PO-4501234567',
        '20',
        'PART-ABC-20',
        'USB-C Cable 1m',
        '24',
        '8',
        'CN',
        '',
    ],
];

/** A value is CSV-escaped only when it contains a comma, quote, or newline. */
function csvCell(value: string): string {
    return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadAsnTemplate() {
    const lines = [ASN_TEMPLATE_HEADERS, ...ASN_TEMPLATE_ROWS].map((row) =>
        row.map(csvCell).join(','),
    );
    downloadCsv('asn-template.csv', `${lines.join('\n')}\n`);
}

// ── Upload ASN file (wired to the real upload service) ────────────────────────
function UploadAsn() {
    const inputRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [dragging, setDragging] = useState(false);

    const onPick = (f: File | undefined) => {
        if (!f) return;
        getAnalyticsService().track('asn_upload_file_selected');
        setFile(f);
        setFileName(f.name);
        setSubmitted(false);
        setStatus(null);
        // Accept the file either way, but warn when it exceeds the limit so the
        // user knows before submitting. Mirrors the "up to 25 MB" hint.
        if (f.size > MAX_UPLOAD_BYTES) {
            setError(
                `That file is ${formatBytes(f.size)}, over the 25 MB limit. ` +
                    'You can still try to submit, but it may fail — consider splitting it or ' +
                    'using the presigned-URL channel for larger files.',
            );
        } else {
            setError(null);
        }
    };

    const reset = () => {
        setFile(null);
        setFileName(null);
        setSubmitted(false);
        setStatus(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const submit = async () => {
        if (!file) return;
        setError(null);
        setStatus(null);
        setProgress(0);
        try {
            await getUploadService().uploadFile(file, setProgress);
            setSubmitted(true);
            setStatus(`Uploaded ${file.name} successfully.`);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Upload failed.');
        } finally {
            setProgress(null);
        }
    };

    if (submitted) {
        return (
            <Box textAlign="center" padding="xl">
                <SpaceBetween size="m">
                    <StatusIndicator type="success">ASN submitted</StatusIndicator>
                    <Box color="text-body-secondary">{status}</Box>
                    <Button onClick={reset}>Upload another file</Button>
                </SpaceBetween>
            </Box>
        );
    }

    let dropLabel = 'Drop your ASN file here or browse';
    if (dragging) dropLabel = 'Drop to upload';
    else if (fileName) dropLabel = 'Choose a different file';

    return (
        <SpaceBetween size="l">
            <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                onDragEnter={(ev) => {
                    ev.preventDefault();
                    setDragging(true);
                }}
                onDragOver={(ev) => {
                    ev.preventDefault();
                    if (!dragging) setDragging(true);
                }}
                onDragLeave={(ev) => {
                    if (!ev.currentTarget.contains(ev.relatedTarget as Node | null)) {
                        setDragging(false);
                    }
                }}
                onDrop={(ev) => {
                    ev.preventDefault();
                    setDragging(false);
                    onPick(ev.dataTransfer.files?.[0]);
                }}
                style={{
                    cursor: 'pointer',
                    border: `2px dashed ${dragging ? '#0972d3' : '#b6bec9'}`,
                    borderRadius: 12,
                    background: dragging ? '#f0f8ff' : '#fbfbfb',
                    padding: '32px 24px',
                    textAlign: 'center',
                }}
            >
                <SpaceBetween size="xs">
                    <Box fontSize="heading-l">⬆</Box>
                    <Box variant="h3">{dropLabel}</Box>
                    <Box color="text-body-secondary">CSV or Excel (.csv, .xlsx) · up to 25 MB</Box>
                </SpaceBetween>
                <input
                    id="asn-file-input"
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }}
                    onChange={(ev) => onPick(ev.target.files?.[0])}
                />
            </div>

            {/* Not sure of the format? Download a ready-made template with the
                exact columns and an example row, fill in your data, and upload. */}
            <Box color="text-body-secondary" fontSize="body-s" textAlign="center">
                Not sure what to upload?{' '}
                <Button variant="inline-link" iconName="download" onClick={downloadAsnTemplate}>
                    Download CSV template
                </Button>{' '}
                — fill in the columns and upload. Excel users can open the CSV and save it as .xlsx.
            </Box>

            {fileName ? (
                <Container>
                    <SpaceBetween size="m">
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                            <SpaceBetween size="xxxs">
                                <Box fontWeight="bold">{fileName}</Box>
                                {file ? (
                                    <Box color="text-body-secondary" fontSize="body-s">
                                        {formatBytes(file.size)}
                                    </Box>
                                ) : null}
                            </SpaceBetween>
                            <Badge color="blue">Ready to submit</Badge>
                        </SpaceBetween>
                        {progress !== null ? (
                            <ProgressBar value={progress} label="Uploading" />
                        ) : null}
                        {error ? <Alert type="error">{error}</Alert> : null}
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button onClick={reset}>Remove file</Button>
                            <Button variant="primary" onClick={submit} disabled={progress !== null}>
                                Submit ASN
                            </Button>
                        </SpaceBetween>
                    </SpaceBetween>
                </Container>
            ) : null}
        </SpaceBetween>
    );
}

// ── Failed transactions drawer ────────────────────────────────────────────────
// Reverse of SOURCE_BY_INTEGRATION: backend source key -> UI integration label.
const INTEGRATION_BY_SOURCE: Record<string, Integration> = {
    API: 'API',
    FILE_DROP: 'File Drop',
    PRESIGNED_URL: 'Presigned URL',
};

const FAILURE_PAGE_SIZE = 50;

// One fetched row plus its resolved bucket, so the panel can filter/label it.
interface LoadedFailure extends AsnFailure {
    bucket: Bucket;
}

/**
 * Modal listing failed submissions for the selected range. Fetches from the
 * failures API for the selected source + bucket (iterating the relevant
 * combinations when "all" is chosen), honors the dashboard's login filter, and
 * pages via the backend cursor ("Load more").
 */
function FailuresPanel({
    range,
    login,
    initIntegration,
    initBucket,
    onClose,
}: {
    range: ActiveRange;
    login?: string;
    initIntegration: Integration | 'all';
    initBucket: Bucket | 'all';
    onClose: () => void;
}) {
    const [q, setQ] = useState('');
    const [integration, setIntegration] = useState<Integration | 'all'>(initIntegration);
    const [bucket, setBucket] = useState<Bucket | 'all'>(initBucket);

    const [rows, setRows] = useState<LoadedFailure[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Per (source, bucket) next-page cursor; presence of any means "more".
    const [cursors, setCursors] = useState<Record<string, string>>({});

    const activeSources = (): string[] =>
        integration === 'all'
            ? INTEGRATIONS.map((i) => SOURCE_BY_INTEGRATION[i])
            : [SOURCE_BY_INTEGRATION[integration]];
    const activeBuckets = (): Bucket[] => (bucket === 'all' ? ['data', 'business'] : [bucket]);

    const load = React.useCallback(
        async (reset: boolean) => {
            const { from, to } = rangeToWindow(range);
            const svc = getAsnMetricsService();

            const combos = activeSources().flatMap((source) =>
                activeBuckets().map((b) => ({ source, b, key: `${source}#${b}` })),
            );
            const toFetch = reset ? combos : combos.filter((c) => cursors[c.key]);
            if (toFetch.length === 0) return;

            setLoading(true);
            setError(null);
            try {
                const pages = await Promise.all(
                    toFetch.map((c) =>
                        svc
                            .getFailures({
                                source: c.source,
                                bucket: c.b,
                                from,
                                to,
                                identityId: login || undefined,
                                limit: FAILURE_PAGE_SIZE,
                                cursor: reset ? undefined : cursors[c.key],
                            })
                            .then((page) => ({ combo: c, page })),
                    ),
                );

                const collected = pages.flatMap(({ combo, page }) =>
                    page.failures.map((f) => ({ ...f, bucket: combo.b })),
                );
                const nextCursors = pages.reduce<Record<string, string>>((acc, { combo, page }) => {
                    if (page.nextCursor) acc[combo.key] = page.nextCursor;
                    return acc;
                }, {});

                setRows((prev) => (reset ? collected : [...prev, ...collected]));
                setCursors((prev) => (reset ? nextCursors : { ...prev, ...nextCursors }));
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Failed to load failures.');
            } finally {
                setLoading(false);
            }
        },
        [range, login, integration, bucket, cursors],
    );

    const resetKey = `${rangeLabel(range)}#${login ?? ''}#${integration}#${bucket}`;
    const loadRef = React.useRef(load);
    loadRef.current = load;
    useEffect(() => {
        setRows([]);
        setCursors({});
        loadRef.current(true).catch(() => undefined);
    }, [resetKey]);

    const hasMore = Object.keys(cursors).length > 0;

    const query = q.trim().toLowerCase();
    const visible = query
        ? rows.filter(
              (r) =>
                  r.submissionId.toLowerCase().includes(query) ||
                  r.asnNumber.toLowerCase().includes(query) ||
                  r.error.toLowerCase().includes(query),
          )
        : rows;

    return (
        <Modal
            visible
            onDismiss={onClose}
            size="max"
            header={
                <Header
                    variant="h2"
                    description={`Range: ${rangeLabel(range)} · Data errors = what you sent, Business errors = order match`}
                >
                    Failed transactions
                </Header>
            }
        >
            <SpaceBetween size="l">
                <ColumnLayout columns={3} variant="text-grid">
                    {INTEGRATIONS.map((i) => {
                        const src = SOURCE_BY_INTEGRATION[i];
                        const n = visible.filter((r) => r.source === src).length;
                        return (
                            <SpaceBetween key={i} size="xxxs">
                                <Box fontSize="heading-l" fontWeight="bold">
                                    {n}
                                    {hasMore ? '+' : ''}
                                </Box>
                                <Box color="text-body-secondary" fontSize="body-s">
                                    {i}
                                </Box>
                            </SpaceBetween>
                        );
                    })}
                </ColumnLayout>

                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <SegmentedControl
                        selectedId={bucket}
                        onChange={({ detail }) => setBucket(detail.selectedId as Bucket | 'all')}
                        options={[
                            { id: 'all', text: 'All types' },
                            { id: 'data', text: 'Data errors' },
                            { id: 'business', text: 'Business errors' },
                        ]}
                    />
                    <SegmentedControl
                        selectedId={integration}
                        onChange={({ detail }) =>
                            setIntegration(detail.selectedId as Integration | 'all')
                        }
                        options={[
                            { id: 'all', text: 'All' },
                            ...INTEGRATIONS.map((i) => ({ id: i, text: i })),
                        ]}
                    />
                </SpaceBetween>

                <Table
                    variant="embedded"
                    items={visible}
                    loading={loading}
                    loadingText="Loading…"
                    filter={
                        <TextFilter
                            filteringText={q}
                            filteringPlaceholder="Search by ASN, PO, or line #…"
                            onChange={({ detail }) => setQ(detail.filteringText)}
                        />
                    }
                    empty={
                        <Box textAlign="center" color="text-body-secondary" padding="l">
                            {error ?? 'No failed transactions in this range.'}
                        </Box>
                    }
                    columnDefinitions={[
                        { id: 'submission', header: 'Submission', cell: (r) => r.submissionId },
                        { id: 'asn', header: 'ASN', cell: (r) => r.asnNumber || '—' },
                        {
                            id: 'integration',
                            header: 'Integration',
                            cell: (r) => INTEGRATION_BY_SOURCE[r.source] ?? r.source,
                        },
                        {
                            id: 'received',
                            header: 'Received',
                            cell: (r) => new Date(r.receivedAt).toLocaleString(),
                        },
                        {
                            id: 'error',
                            header: 'Error',
                            cell: (r) => (
                                <StatusIndicator type={r.bucket === 'data' ? 'warning' : 'error'}>
                                    {r.error || '—'}
                                </StatusIndicator>
                            ),
                        },
                    ]}
                />
                {error && visible.length > 0 ? <Alert type="error">{error}</Alert> : null}
                {hasMore && !loading ? (
                    <Box textAlign="center">
                        <Button onClick={() => load(false).catch(() => undefined)}>
                            Load more
                        </Button>
                    </Box>
                ) : null}
            </SpaceBetween>
        </Modal>
    );
}

FailuresPanel.defaultProps = {
    login: undefined,
};

// ── Main screen ───────────────────────────────────────────────────────────────
export default function AsnScreen() {
    const [range, setRange] = useState<ActiveRange>({ kind: 'preset', preset: '7d' });
    const [showCustom, setShowCustom] = useState(false);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [drawer, setDrawer] = useState<{
        integration: Integration | 'all';
        bucket: Bucket | 'all';
    } | null>(null);

    const customDays = daysBetween(from, to);
    const applyCustom = () => {
        if (customDays > 0) setRange({ kind: 'custom', from, to, days: customDays });
    };

    // Per-integration counts for the selected range, fetched from the metrics API.
    const [metrics, setMetrics] = useState<AsnMetrics | null>(null);
    const [metricsError, setMetricsError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        const { from: f, to: t } = rangeToWindow(range);
        setMetricsError(null);
        getAsnMetricsService()
            .getMetrics(f, t)
            .then((m) => {
                if (active) setMetrics(m);
            })
            .catch((e) => {
                if (active)
                    setMetricsError(e instanceof Error ? e.message : 'Failed to load metrics.');
            });
        return () => {
            active = false;
        };
    }, [range]);

    const countsFor = (intg: Integration): IntegrationCounts =>
        metrics?.byIntegration[SOURCE_BY_INTEGRATION[intg]] ?? EMPTY_COUNTS;

    const totalFailures = INTEGRATIONS.reduce((sum, intg) => {
        const c = countsFor(intg);
        return sum + c.dataErrors + c.businessErrors;
    }, 0);

    const rangeControl = (
        <SegmentedControl
            selectedId={range.kind === 'custom' ? 'custom' : range.preset}
            onChange={({ detail }) => {
                if (detail.selectedId === 'custom') {
                    setShowCustom(true);
                } else {
                    setRange({ kind: 'preset', preset: detail.selectedId as Preset });
                    setShowCustom(false);
                }
            }}
            options={[
                ...PRESETS.map((p) => ({ id: p, text: p })),
                { id: 'custom', text: 'Custom' },
            ]}
        />
    );

    return (
        <SpaceBetween size="l">
            {drawer ? (
                <FailuresPanel
                    range={range}
                    initIntegration={drawer.integration}
                    initBucket={drawer.bucket}
                    onClose={() => setDrawer(null)}
                />
            ) : null}

            <Header
                variant="h1"
                description="See what's pending, upload new ASN files, and review your submission summary."
                actions={
                    <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                        <Button
                            iconName={totalFailures ? 'status-warning' : undefined}
                            onClick={() => setDrawer({ integration: 'all', bucket: 'all' })}
                        >
                            View all failures ({totalFailures})
                        </Button>
                        {rangeControl}
                    </SpaceBetween>
                }
            >
                ASN — Advance Shipping Notice
            </Header>

            {showCustom ? (
                <Container>
                    <SpaceBetween size="s" direction="horizontal" alignItems="end">
                        <div style={{ width: 180 }}>
                            <Box variant="awsui-key-label">From</Box>
                            <DatePicker
                                value={from}
                                onChange={({ detail }) => setFrom(detail.value.replace(/\//g, '-'))}
                                placeholder="YYYY/MM/DD"
                            />
                        </div>
                        <div style={{ width: 180 }}>
                            <Box variant="awsui-key-label">To</Box>
                            <DatePicker
                                value={to}
                                onChange={({ detail }) => setTo(detail.value.replace(/\//g, '-'))}
                                placeholder="YYYY/MM/DD"
                            />
                        </div>
                        <Button variant="primary" onClick={applyCustom} disabled={customDays === 0}>
                            Apply
                        </Button>
                        <Box color={customDays > 0 ? 'text-body-secondary' : 'text-status-error'}>
                            {customDays > 0
                                ? `${customDays} day(s) selected`
                                : 'Select a valid range (To on/after From)'}
                        </Box>
                    </SpaceBetween>
                </Container>
            ) : null}

            <Header variant="h2">Submission summary by integration ({rangeLabel(range)})</Header>
            <ColumnLayout columns={3}>
                {INTEGRATIONS.map((intg) => (
                    <IntegrationCard
                        key={intg}
                        intg={intg}
                        counts={countsFor(intg)}
                        onViewFailures={(bucket) => setDrawer({ integration: intg, bucket })}
                    />
                ))}
            </ColumnLayout>
            {metricsError ? <Alert type="error">{metricsError}</Alert> : null}

            <PendingAsns />

            <Container
                header={
                    <Header
                        variant="h2"
                        description="Drop in a CSV or Excel ASN. We validate it, then it appears in the summary above."
                    >
                        Upload ASN file
                    </Header>
                }
            >
                <UploadAsn />
            </Container>
        </SpaceBetween>
    );
}
